import axiosClient from './axios-client';
import type { PagedListResponse, PaginationParams } from './common.dto';
import { API_ENDPOINTS } from '@/constants';

export type UserRole = 'user' | 'staff' | 'admin';

export interface User {
  id: string;
  email: string;
  userRole: UserRole;
  isSalesperson: boolean;
  isWarehouseHandler: boolean;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  firstName: string;
  lastName: string;
  phone?: string;
  country: string;
  state?: string;
  zipCode?: string;
  city?: string;
  company?: string;
  address?: string;
}

export interface UpdateUserRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  country?: string;
  state?: string;
  zipCode?: string;
  city?: string;
  company?: string;
  address?: string;
}

export interface CreateUserRequest {
  email: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  country?: string;
  state?: string;
  zipCode?: string;
  city?: string;
  company?: string;
  address?: string;
  userRole?: UserRole;
  isSalesperson?: boolean;
  isWarehouseHandler?: boolean;
  status?: 'active' | 'inactive';
}

export interface UpdateUserAdminRequest extends Partial<CreateUserRequest> {}

export interface ChangePasswordRequest {
  confirmPassword: string;
  newPassword: string;
}

export interface UserParams extends PaginationParams {
  userRole?: UserRole;
  roleIn?: string;
  isSalesperson?: boolean;
}

export const usersApi = {
  getUsers: async (params?: UserParams): Promise<PagedListResponse<User>> => {
    const response = await axiosClient.get(API_ENDPOINTS.USERS, { params });
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await axiosClient.get(API_ENDPOINTS.USER_PROFILE);
    return response.data;
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await axiosClient.get(API_ENDPOINTS.USERS_BY_ID(id));
    return response.data;
  },

  updateProfile: async (data: UpdateUserRequest): Promise<User> => {
    const response = await axiosClient.patch(API_ENDPOINTS.USER_PROFILE, data);
    return response.data;
  },

  changePassword: async (
    data: ChangePasswordRequest
  ): Promise<{ message: string }> => {
    const response = await axiosClient.patch(
      API_ENDPOINTS.USER_CHANGE_PASSWORD,
      data
    );
    return response.data;
  },

  createUser: async (data: CreateUserRequest): Promise<User> => {
    const response = await axiosClient.post(API_ENDPOINTS.USERS, data);
    return response.data;
  },

  updateUser: async (id: string, data: UpdateUserAdminRequest): Promise<User> => {
    const response = await axiosClient.patch(`${API_ENDPOINTS.USERS}/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: string): Promise<{ message: string }> => {
    const response = await axiosClient.delete(`${API_ENDPOINTS.USERS}/${id}`);
    return response.data;
  },
};
